import { ConfirmationDialog } from '@components/ConfirmationDialog';
import { DropdownContext, type ItemAction } from '@components/DropdownContext';
import type { TextFieldPropOnChange, TextFieldPropStatus, TextFieldPropValue } from '@components/FieldNumberFormat';
import { TextWithTooltip } from '@components/WithTooltip';
import type { ProjectMethodologyEnum } from '@gql/graphql';
import { useAccess } from '@hooks/useAccess';
import { useDropdownContextMenuTooltipController } from '@hooks/useDropdownContextMenuTooltipController';
import { useIsIntersecting } from '@hooks/useIsIntersecting';
import { useMethodsConfirmationDialog } from '@hooks/useMethodsConfirmationDialog';
import { cnListItem, cnListItemGrid, cnMixSpace, cnText, Tooltip } from '@uikit';

import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import Styles from '../catalogCommonStyles.module.css';
import { CatalogEditValueInput } from '../CatalogEditValueInput';
import type { TCheckIsUniqueProps } from '../CatalogOthersType/hooks';

type TTypeItemRequestHookProps = {
    onSuccess: () => void;
};

export type TTypeItemEditVariables = {
    id?: number;
    name: string;
    methodology: ProjectMethodologyEnum;
};

export type TTypeItem = {
    id: number;
    name: string;
    canDelete: boolean;
    isSystem: boolean;
};

type TTypeItemProps = {
    item: TTypeItem;
    updateTypesData: () => void;
    methodology: ProjectMethodologyEnum;

    checkIsUniqueName: (props: TCheckIsUniqueProps) => { isUniqueName: boolean };
    edit: (props: TTypeItemRequestHookProps) => (variables: TTypeItemEditVariables) => void;
    remove: (props: TTypeItemRequestHookProps) => (id: number) => void;

    editTooltipMsg: string;
    editPlaceholder: string;
    removeQuestion: string;
    removeHeader: string;
    cantDeleteTooltip: string;

    listWrapper?: HTMLElement | null;
};

const MAX_NAME_LENGTH = 255;

export const TypeItem = ({
    item,
    methodology,
    updateTypesData,
    checkIsUniqueName,
    edit,
    remove,
    editTooltipMsg,
    editPlaceholder,
    removeQuestion,
    removeHeader,
    listWrapper,
    cantDeleteTooltip,
}: TTypeItemProps) => {
    const { catalogs: catalogsRules } = useAccess();

    const { id, name, isSystem, canDelete } = item;

    const [mode, setMode] = useState<'read' | 'edit'>('read');
    const [inputStatus, setInputStatus] = useState<TextFieldPropStatus | undefined>(undefined);
    const [newName, setNewName] = useState<TextFieldPropValue>(() => name);

    const [tooltipMsg, setTooltipMsg] = useState<string | null>(null);

    const resetState = useCallback(() => {
        setMode('read');
        setInputStatus(undefined);
    }, []);

    const handleEditGroup = useCallback(() => {
        setMode('edit');
    }, []);

    const isInputChanged = useMemo(() => name !== newName, [name, newName]);

    const { isUniqueName } = checkIsUniqueName({
        name: mode === 'edit' ? newName ?? '' : '',
        methodology,
        isInputChanged,
    });

    const handleSubmit = edit({
        onSuccess: useCallback(() => {
            resetState();
            updateTypesData();
        }, [resetState, updateTypesData]),
    });

    const handleRemove = remove({
        onSuccess: updateTypesData,
    });

    const getErrorMessage = useCallback(() => {
        const nameLength = newName?.trim().length ?? 0;

        if (!nameLength) {
            return 'Поле не может быть пустым';
        }

        if (nameLength > MAX_NAME_LENGTH) {
            return `Не более ${MAX_NAME_LENGTH} символов`;
        }

        if (!isUniqueName) {
            return editTooltipMsg;
        }

        return null;
    }, [newName, isUniqueName, editTooltipMsg]);

    const handleSave = useCallback(() => {
        if (!isInputChanged) {
            resetState();

            return;
        }

        const errorMsg = getErrorMessage();

        if (errorMsg) {
            setTooltipMsg(errorMsg);
            setInputStatus('alert');

            return;
        }

        if (!newName) {
            return;
        }

        handleSubmit({ id, methodology, name: newName });
    }, [id, methodology, newName, getErrorMessage, handleSubmit, isInputChanged, resetState]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => handleRemove(id),
        });

    const dropdownItems = useMemo<ItemAction[]>(
        () => [
            {
                label: 'Редактировать',
                action: handleEditGroup,
                disabled: isSystem || !catalogsRules.canEditLimited,
            },
            {
                label: 'Удалить',
                action: handleOpenConfirmationDialog,
                disabled: isSystem || !canDelete || !catalogsRules.canDeleteLimited,
                haveTooltip: !canDelete && catalogsRules.canDeleteLimited,
                tooltipName: 'cantDelete',
                tooltipMsg: cantDeleteTooltip,
            },
        ],
        [
            canDelete,
            catalogsRules.canDeleteLimited,
            handleEditGroup,
            handleOpenConfirmationDialog,
            isSystem,
            catalogsRules.canEditLimited,
            cantDeleteTooltip,
        ],
    );

    const handleChangeNewName = useCallback<TextFieldPropOnChange>(({ value }) => {
        setNewName(value);
        setInputStatus(undefined);
        setTooltipMsg(null);
    }, []);

    const handleCancelEditGroup = useCallback(() => {
        setNewName(name);
        resetState();
    }, [name, resetState]);

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    const { refCallback, isIntersecting } = useIsIntersecting(listWrapper);

    return mode === 'read' ? (
        <div
            className={cn(
                cnText({
                    lineHeight: 'xs',
                    size: 's',
                    view: 'primary',
                }),
                cnListItem(),
                cnMixSpace({ pV: 'xs', pH: 's' }),
                cnListItemGrid(),
            )}
        >
            <div className={Styles.content}>
                <TextWithTooltip truncate showMode="overflow" tooltipText={name} text={name} />

                {!isSystem && (
                    <DropdownContext
                        getItemAttributesHandler={getMenuItemAttributes}
                        onMouseMoveHandler={onMenuMouseMove}
                        onMouseLeaveHandler={onMenuMouseLeave}
                        items={dropdownItems}
                        direction="downStartRight"
                    />
                )}
                {activeTooltipMsg && (
                    <Tooltip
                        size="m"
                        direction="upCenter"
                        position={tooltipPosition}
                        isInteractive={false}
                        style={{ zIndex: 2 }}
                    >
                        {activeTooltipMsg}
                    </Tooltip>
                )}
                <ConfirmationDialog
                    textHeader={removeHeader}
                    textConfirmation={removeQuestion}
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                    isOpened={isOpenConfirmationDialog}
                    onSubmit={handleActionQuery}
                    onClose={handleCloseConfirmationDialog}
                />
            </div>
        </div>
    ) : (
        <div ref={refCallback}>
            <CatalogEditValueInput
                value={newName}
                onChange={handleChangeNewName}
                onCancel={handleCancelEditGroup}
                onSubmit={handleSave}
                status={inputStatus}
                tooltipMsg={tooltipMsg ?? ''}
                inputPlaceholder={editPlaceholder}
                isTooltipVisible={isIntersecting}
            />
        </div>
    );
};

TypeItem.displayName = 'TypeItem';
